import { toKebabCase, toSnakeCase } from './convert';

/**
 * Generates a native Promise with exposed resolve and reject methods
 * This is used to mimic the behavior of
 * {@link https://docs.angularjs.org/api/ng/service/$q#defer AngularJS $q.defer()}
 * @returns {Object} Object with exposed promise, resolve and reject methods
 */
export const getDeferredPromise = () => {
  let deferredResolve = null;
  let deferredReject = null;
  const promise = new Promise((resolve, reject) => {
    deferredResolve = resolve;
    deferredReject = reject;
  });
  return {
    promise,
    resolve: deferredResolve,
    reject: deferredReject,
  };
};

/**
 * This method is intended for encoding *key* or *value* parts of query component.
 * NOTE: This method is old and copied from AngularJS source code.
 * Semicolons stay percent-encoded so they are not treated as parameter separators.
 * @param {string} val Value to be encoded
 * @param {boolean} pctEncodeSpaces
 * @returns {string} Encoded value
 */
export const encodeUriQuery = (val, pctEncodeSpaces) => encodeURIComponent(val)
  .replace(/%40/gi, '@')
  .replace(/%3A/gi, ':')
  .replace(/%24/g, '$')
  .replace(/%2C/gi, ',')
  .replace(/%2F/gi, '/')
  .replace(/%20/g, (pctEncodeSpaces ? '%20' : '+'));

/**
 * Generates a UUID
 * Based on {@link https://www.ietf.org/rfc/rfc4122.txt RFC4122}
 * @returns {string} Generated globally unique identifier
 */
export const generateUuid = () => 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
  /* eslint-disable no-bitwise, no-mixed-operators */
  const r = Math.random() * 16 | 0;
  const v = c === 'x' ? r : (r & 0x3 | 0x8);
  /* eslint-enable no-bitwise, no-mixed-operators */
  return v.toString(16);
});

const UUID_REGEX = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

/**
 * Tests if a given string value is in valid valid UUID format
 * @param {string} value Value to check
 * @returns {boolean}
 */
export const isValidUuid = (value) => value && UUID_REGEX.test(value);

/**
 * The function collects the changed fields on the form
 * @param {Object.<string, *>[]|undefined} controls Controls
 * @returns {Object.<string, *>}
 */
export const collectChangedFields = (controls = []) => {
  const collectedTouchedFields = controls.filter(({ $pristine }) => !$pristine);
  const formattedTouchedFields = collectedTouchedFields.reduce((acc, { $name, $modelValue }) => {
    const name = toKebabCase($name);
    return ({ ...acc, [name]: $modelValue });
  }, {});

  return formattedTouchedFields;
};

/**
 * The function transforms keys of the object to kebab case and return new updated object
 * @param {Object.<string, *>} obj 
 * @returns {Object.<string, *>}
 */
export const transformObjectKeyToKebab = (obj) => {
  const entries = Object.entries(obj);

  const transformedObject = {};

  entries.forEach(([key, value]) => {
    transformedObject[toKebabCase(key)] = value;
  });

  return transformedObject;
}

/**
 * The function collects uniq sheet uuids from rules of schedule and sheet
 * @param {(import('../@types/application/schemas').TotalSchedule)|undefined} schedule
 * @param {(import('../@types/application/schemas').TotalSheet)|undefined} sheet
 * @returns {Set<string>}
 */
export const collectUniqUuidSheetsFromSheetAndScheduleRules = (schedule, sheet) => {
  const selectedSheetUuids = new Set();

  if (schedule) {
    schedule.entries.forEach(({ sheet: { uuid } }) => !selectedSheetUuids.has(uuid) && selectedSheetUuids.add(uuid));
  } else if (sheet?.uuid) {
    selectedSheetUuids.add(sheet?.uuid);
  }

  return selectedSheetUuids;
};

/**
 * The function checks whether the selected sheet is contained in the sheet collection
 * @param {(import('../@types/application/schemas').TotalSchedule)|undefined} defaultPosTotalSchedule
 * @param {(import('../@types/application/schemas').TotalSheet)|undefined} defaultPosTotalSheet
 * @param {string|undefined} selectedSheetUuid
 * @returns {boolean}
 */
// eslint-disable-next-line max-len
export const checkSelectedSheetInSheetCollection = (defaultPosTotalSchedule, defaultPosTotalSheet, selectedSheetUuid) => selectedSheetUuid && collectUniqUuidSheetsFromSheetAndScheduleRules(defaultPosTotalSchedule, defaultPosTotalSheet).has(selectedSheetUuid);

/**
 * This function Base64 encodes a text file.
 * @param {file} textFile
 * @returns {string} base64 encoded string
 */
export const encodeTextFileToBase64 = (textFile) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const base64String = btoa(encodeURIComponent(reader.result));
        resolve(base64String);
      } catch(e) {
        reject(e);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(textFile);
  });

export const DATE_FORMAT = 'YYYY-MM-DD';
export const CAL_FORMAT = 'MM-DD-YYYY';

/**
 * This function parses an array of date range objects.
 * It will change Date fields into date strings with format 'YYYY-MM-DD'
 * and serialize open-ended ranges with a null end date.
 * @param {dateRanges} array
 * @returns {array} date range objects
 */
const isEmptyDateValue = (value) => value === '' || value == null;

const formatDateRangeFieldValue = (key, value) => {
  if (key === 'endDate' && isEmptyDateValue(value)) {
    return null;
  }

  if (value instanceof Date) {
    return moment(value).format(DATE_FORMAT);
  }

  // JSON.stringify omits undefined keys, which makes the backend treat start_date
  // as missing and crash with a 500 instead of returning a field error.
  if (value === undefined) {
    return null;
  }

  return value;
};

export const parseDateRanges = (dateRanges) =>
  dateRanges.map(({ id, ...dateRange }) => {
    const formattedRange = Object.fromEntries(
      Object.entries(dateRange).map(([key, value]) => [
        key,
        formatDateRangeFieldValue(key, value),
      ]),
    );

    if (!Object.prototype.hasOwnProperty.call(formattedRange, 'startDate')) {
      formattedRange.startDate = null;
    }
    if (!Object.prototype.hasOwnProperty.call(formattedRange, 'endDate')) {
      formattedRange.endDate = null;
    }

    return formattedRange;
  });

export const orderJSON = (jsonData, keyOrder) => Object.fromEntries(
  keyOrder.filter(key => jsonData[key] !== undefined)
    .map(key => [key, jsonData[key]])
);

/**
 * Converts the provided object into an appropriate API payload.
 *
 * This is written as generically as possible - ideally this same logic can
 * be reused for other calls that need to submit a similarly-formatted payload.
 *
 * @template T
 * @param {T} payloadObject The object to convert into a payload.
 * @param {Array<keyof T> | undefined} payloadFields If provided, only tries to convert these fields.
 * @param {string | undefined} currentPrefix An optional prefix to append to each key
 * @returns {Record<string, any>}
 */
export const buildPayload = (payloadObject, payloadFields, currentPrefix = '') => {
  // By default, serialize all keys on this object if a specific list isn't specified.
  const fields =
    payloadFields ||
    Object.keys(payloadObject).filter((key) => key !== 'cls' && key !== 'pk');

  const payload = {};
  fields.forEach((key) => {
    const prop = payloadObject[key];
    // By convention, object props have their keys appended in kebab-case.
    const keyPrefix = `${currentPrefix}${toKebabCase(key)}`;
    // typeof 'object' matches objects, arrays, and null (we love js)
    if (typeof prop === 'object' && prop !== null) {
      if (Array.isArray(prop)) {
        // This property is an array, make a sub-payload for each entry and append it.
        prop.forEach((entry, index) => {
          // By convention, use index if the array entry doesnt have a pk.
          const entryPrefix = `${currentPrefix}${toKebabCase(entry.cls || key)}-${entry.pk || index}-`;
          // Build a payload for this entry and append it to the current payload.
          const entryPayload = buildPayload(entry, undefined, entryPrefix);
          Object.keys(entryPayload).forEach((entryKey) => {
            payload[entryKey] = entryPayload[entryKey];
          });
        });
      } else {
        // This property is an object, iterate through keys and append each.
        const objectPayload = buildPayload(prop);
        Object.keys(objectPayload).forEach((objectKey) => {
          // Edge case - if a key contains snake and kebab casing, it shouldn't be converted further.
          // This should only happen on non-arrayed objects nested more than 2 deep.
          const hasSubObject = objectKey.indexOf('-') >= 0 && objectKey.indexOf('_') >= 0
          const payloadKey = `${keyPrefix}-${hasSubObject ? objectKey : toSnakeCase(objectKey)}`;
          payload[payloadKey] = objectPayload[objectKey];
        });
      }
    } else {
      // This property is a non-object value (primitive, null, etc.), just append it normally.
      // Django form fields use snake_case, so convert camelCase primitives to snake_case.
      const fieldKey = `${currentPrefix}${toSnakeCase(key)}`;
      payload[fieldKey] = prop;
    }
  });

  return payload;
};


/**
 * Compares two shallow (non-nested) objects for equality.
 *
 * Performs a fast, strict equality (`===`) check on each property value.
 * This function is optimized for plain objects containing only primitive
 * values (e.g., integers, strings, booleans). It does **not** perform
 * deep comparisons or handle nested objects, arrays, Maps, or Sets.
 *
 * @function shallowEqual
 * @param {Object} obj1 - The first object to compare.
 * @param {Object} obj2 - The second object to compare.
 * @returns {boolean} Returns `true` if both objects have the same keys
 * and all corresponding values are strictly equal (`===`); otherwise `false`.
 *
 * @example
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 2 });
 * // → true
 *
 * @example
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 3 });
 * // → false
 *
 * @example
 * shallowEqual({ a: 1, b: 2 }, { a: 1, b: 2, c: 3 });
 * // → false
 *
 * @note
 * - Comparison is **order-independent** because key sets are checked.
 * - Only compares **own enumerable properties** (via `Object.keys()`).
 * - Ideal for performance-critical equality checks in UI frameworks
 *   (e.g., React `shouldComponentUpdate`, Angular watchers, etc.).
 *
 * @complexity O(n), where n is the number of keys in the object.
 */
export const shallowEqual = (obj1, obj2) => {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  for (let i = 0; i < keys1.length; i += 1) {
    const key = keys1[i];
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};
